import { useMemo } from 'react';
import { Map as MapIcon, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { UI_LABELS } from '../../../lib/terminology';

export function FocusMap() {
  // Select raw state values - avoid selecting functions to prevent infinite loops
  const itemPools = useItemPoolsStore((state) => state.itemPools);
  const setActiveItemPool = useItemPoolsStore((state) => state.setActiveItemPool);
  const driftEvents = useItemPoolsStore((state) => state.driftEvents);
  
  // Create a helper to get drift events for a specific list using useMemo
  const getDriftEventsForList = useMemo(() => {
    return (listId: string) => driftEvents.filter(e => e.itemPoolId === listId && !e.resolvedAt);
  }, [driftEvents]);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <MapIcon className="h-8 w-8 text-primary-600" />
          <h1 className="text-3xl font-bold text-[var(--text-pri)]">Focus Map</h1>
        </div>
        <p className="text-[var(--text-sec)]">
          Navigate and coordinate across all {UI_LABELS.itemPools}
        </p>
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Total Lists</p>
          <p className="text-3xl font-bold text-[var(--text-pri)]">{itemPools.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Total Priorities</p>
          <p className="text-3xl font-bold text-[var(--text-pri)]">
            {itemPools.reduce((sum, list) => sum + list.priorityIds.length, 0)}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Above the Line</p>
          <p className="text-3xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">
            {itemPools.reduce((sum, list) => {
              // Count priorities that are actually above the line (rank <= cutLinePosition)
              const aboveCount = list.priorityIds.length > 0 
                ? Math.min(list.cutLinePosition, list.priorityIds.length)
                : 0;
              return sum + aboveCount;
            }, 0)}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Below the Line</p>
          <p className="text-3xl font-bold text-[var(--text-sec)]">
            {itemPools.reduce((sum, list) => {
              // Count priorities that are below the line
              const belowCount = list.priorityIds.length > list.cutLinePosition
                ? list.priorityIds.length - list.cutLinePosition
                : 0;
              return sum + belowCount;
            }, 0)}
          </p>
        </div>
      </div>
      
      {/* All item pools. Previously grouped by item_pools.horizon, a field with
          no UI to set it: every real pool held the column default, so the
          headings conveyed nothing. The column and its type were dropped in
          migration 707; this is now a flat list. */}
      {itemPools.length === 0 ? (
        <div className="card text-center py-12">
          <MapIcon className="h-12 w-12 mx-auto mb-3 text-[var(--text-ter)]" />
          <p className="text-[var(--text-sec)]">No {UI_LABELS.itemPools.toLowerCase()} yet</p>
          <p className="text-sm text-[var(--text-ter)] mt-1">Create your first {UI_LABELS.itemPool.toLowerCase()} to get started</p>
        </div>
      ) : (
        <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {itemPools.map((list) => {
                    const driftEvents = getDriftEventsForList(list.id);
                    const aboveCount = list.cutLinePosition;

                    return (
                      <Link
                        key={list.id}
                        to="/prioritization/lists"
                        onClick={() => setActiveItemPool(list.id)}
                        className="card hover:shadow-lg transition-all hover:border-primary-500"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="font-semibold text-[var(--text-pri)]">{list.name}</h3>
                          {driftEvents.length > 0 && (
                            <div title={`${driftEvents.length} drift events`}>
                              <AlertTriangle className="h-5 w-5 text-[var(--coral)]" />
                            </div>
                          )}
                        </div>
                        
                        {list.description && (
                          <p className="text-sm text-[var(--text-sec)] mb-3">{list.description}</p>
                        )}
                        
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="flex items-center gap-1 text-[var(--green)] dark:text-[var(--text-ter)]">
                              <CheckCircle className="h-4 w-4" />
                              <span className="font-medium">{Math.min(aboveCount, list.priorityIds.length)}</span>
                            </div>
                            <p className="text-xs text-[var(--text-sec)]">Above line</p>
                          </div>
                          
                          <div>
                            <div className="flex items-center gap-1 text-[var(--text-sec)]">
                              <Clock className="h-4 w-4" />
                              <span className="font-medium">{Math.max(0, list.priorityIds.length - aboveCount)}</span>
                            </div>
                            <p className="text-xs text-[var(--text-sec)]">Below line</p>
                          </div>
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-[var(--rule)] flex items-center justify-between text-xs text-[var(--text-ter)]">
                          <span>Owner: {list.ownerId}</span>
                          <span className={`px-2 py-1 rounded ${
                            list.status === 'active' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
                            list.status === 'draft' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' :
                            'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                          }`}>
                            {list.status}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
        </div>
      )}
    </div>
  );
}

